/**
 * Holds the current customer for the whole dashboard.
 *
 * Loads the customers the signed-in person may work on, restores the one
 * this browser remembered (or takes the only one), stamps it on the API
 * client, and — on a change — drops every cached query, so nothing of the
 * previous customer survives on screen.
 *
 * It also holds the customer's EDIT LEASE for this tab: taken when the
 * customer is chosen, kept alive by a heartbeat while the tab is visible,
 * dropped when the person changes customer, signs out or closes the tab.
 * When someone else holds it, every page is in viewing mode; the moment the
 * holder leaves, the next heartbeat takes it and the person is told.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/useAuth";
import { CustomerCtx, type CustomerState, type EditState } from "../hooks/useCustomer";
import { useToast } from "../hooks/useToast";
import { api, type CustomerSummary } from "../lib/api";
import { LEASE_HEARTBEAT_MS } from "../lib/config";
import { loadRememberedCustomerId, setCurrentCustomerId } from "../lib/customer";

const UNKNOWN: EditState = { canEdit: true, editor: null, known: false };

export function CustomerProvider({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<CustomerSummary[]>([]);
  // the remembered customer is in place BEFORE the first request leaves:
  // the API client reads the module value, set here synchronously
  const [currentId, setCurrentId] = useState<string | null>(() => {
    const remembered = loadRememberedCustomerId();
    setCurrentCustomerId(remembered);
    return remembered;
  });
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const toast = useToast();
  const [edit, setEdit] = useState<EditState>(UNKNOWN);
  const editRef = useRef<EditState>(UNKNOWN);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.customers();
      setCustomers(list);
      setCurrentId((previous) => {
        const kept = list.find((c) => c.id === previous) ?? (list.length === 1 ? list[0] : null);
        const next = kept?.id ?? null;
        if (next !== previous) {
          // a remembered customer that is gone, or the only one taking over:
          // whatever was fetched meanwhile was for another header
          setCurrentCustomerId(next);
          void queryClient.resetQueries();
        }
        return next;
      });
    } catch {
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [queryClient]);

  // (re)load whenever the session changes: sign-in, sign-out, profile refresh
  useEffect(() => {
    if (status !== "authenticated") {
      setCustomers([]);
      setCurrentId(null);
      setCurrentCustomerId(null);
      return;
    }
    void load();
  }, [status, user?.email, load]);

  // the module value the API client reads, kept in step with the state
  useEffect(() => {
    setCurrentCustomerId(currentId);
  }, [currentId]);

  // The edit lease of the chosen customer, for this tab: acquire, heartbeat
  // while visible, release on the way out. Keyed on the customer id only, so
  // a refresh of the customer list never drops and retakes the lease.
  const hasCurrent = currentId != null && customers.some((c) => c.id === currentId);
  useEffect(() => {
    if (!currentId || !hasCurrent) {
      editRef.current = UNKNOWN;
      setEdit(UNKNOWN);
      return;
    }
    const id = currentId;
    let cancelled = false;
    const beat = async () => {
      try {
        const state = await api.acquireLease(id);
        if (cancelled) return;
        const next: EditState = {
          canEdit: state.mine,
          editor: state.mine ? null : state.holder_name,
          known: true,
        };
        const previous = editRef.current;
        editRef.current = next;
        setEdit(next);
        if (previous.known && !previous.canEdit && next.canEdit) {
          toast(t("customer.nowEditing"), "success");
        }
      } catch {
        // keep what we knew; the server guards every write regardless
      }
    };
    void beat();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void beat();
    }, LEASE_HEARTBEAT_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void beat();
    };
    // the tab closes or navigates away: let the lease go at once, not at expiry
    const onHide = () => {
      void api.releaseLease(id, true).catch(() => undefined);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pagehide", onHide);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pagehide", onHide);
      editRef.current = UNKNOWN;
      setEdit(UNKNOWN);
      void api.releaseLease(id).catch(() => undefined);
    };
  }, [currentId, hasCurrent, t, toast]);

  const select = useCallback(
    (id: string) => {
      if (id === currentId) return;
      setCurrentCustomerId(id);
      setCurrentId(id);
      // nothing of the previous customer may survive on screen: every
      // mounted query refetches under the new header (a plain cache clear
      // left the old rows in the components that held them), and the pages
      // start again from the new customer's dashboard
      void queryClient.resetQueries();
      void navigate("/");
    },
    [currentId, queryClient, navigate],
  );

  const value = useMemo<CustomerState>(
    () => ({
      customers,
      current: customers.find((c) => c.id === currentId) ?? null,
      loading,
      select,
      refresh: load,
      edit,
    }),
    [customers, currentId, loading, select, load, edit],
  );

  return <CustomerCtx.Provider value={value}>{children}</CustomerCtx.Provider>;
}
